import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useFramesStore } from "@/stores/frames-store";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import {
  focusPresentedFramesInDesktopMode,
  shouldUseDesktopExpandedPresentation,
} from "@/lib/focus/focus-desktop-mode";
import type { PresentFramesRequest } from "@/lib/canvas-presentation-types";
import type { PresentationState } from "@/lib/focus/focus-types";
import {
  calculateContentAwareFrameBox,
  resolveAutoSide,
  type ContentFitPlacement,
} from "@/lib/canvas-presentation-layout";
import { isDockedSize } from "@/lib/focus/focus-container-boxes";
import {
  enterPresentationMode,
  exitPresentationMode,
} from "@/lib/presentation-mode";
import {
  focusOriginOf,
  focusRoundOwnerOf,
  stampAgentRound,
} from "@/lib/agent-focus-origin";
import {
  framesKeptByRound,
  planClearAgentFrames,
  planFocusRound,
} from "@/lib/focus/focus-plan-rounds";
import { commitPresentation } from "@/lib/focus/focus-adapter";
import {
  currentFocusPlanEnv,
  frameNaturalSize,
} from "@/lib/focus/focus-natural-size";
import { isFrameHiddenByMetadata } from "@/stores/frames-selectors";
import { setFrameHidden } from "@/lib/frame-visibility";
import { unhideCanvasFrames } from "@/lib/reveal-canvas-frames";
import { applyFocusLayoutToFrames } from "@/lib/focus-window-presentation";
import { measureAgentCursorBubble } from "@/desktop/frame-presentation-measurement";
import {
  armAgentContentRefit,
  measureAgentContent,
} from "@/lib/focus/agent-content-refit";
import {
  canvasFocusPresentation,
  focusRendererKind,
} from "@/lib/focus/focus-renderer-kind";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { getWindowOrigin } from "@/desktop/use-window-origin";
import {
  currentFramePresentationSurface,
  localFramePresentationExclusions,
  type GlobalPresentationRect,
} from "@/desktop/frame-presentation-surface";

function focusWindowModeForSide(side: PresentFramesRequest["placement"]) {
  if (side === "left" || side === "right") return side;
  return null;
}

function focusWindowModeForRequest(
  request: PresentFramesRequest,
  targetDisplayId: number | null | undefined,
) {
  const placement = request.placement ?? "center";
  if (placement === "left" || placement === "right") return placement;
  if (placement !== "cursor" && placement !== "auto") return null;
  const cursorPoint = request.cursorPoint;
  const cursorPointMatchesTarget =
    cursorPoint &&
    (targetDisplayId == null ||
      cursorPoint.displayId == null ||
      cursorPoint.displayId === targetDisplayId);
  if (!cursorPointMatchesTarget) return null;
  const viewportRect = currentFramePresentationSurface(request.fitToDisplay);
  const origin = getWindowOrigin();
  const anchor = { x: cursorPoint.x - origin.x, y: cursorPoint.y - origin.y };
  return focusWindowModeForSide(resolveAutoSide(viewportRect, anchor));
}

function restoreFocusHiddenFramesFor(frameIds: string[]) {
  if (!canvasFocusPresentation(usePresentationStore.getState().presentation)) {
    return;
  }
  const focused = new Set(frameIds);
  useCanvasStore
    .getState()
    .setFocusHiddenFrames(
      useFramesStore.getState().frameOrder.filter((id) => !focused.has(id)),
    );
}

function contentAwareFitForRequest(
  frameId: string,
  request: PresentFramesRequest,
  options: { targetDisplayId?: number | null; mountsLater: boolean },
) {
  if (!useFramesStore.getState().framesById[frameId]) return undefined;
  const measured = measureAgentContent(
    frameId,
    request.desiredContentSize,
    request.fitToDisplay,
  );
  const origin = getWindowOrigin();
  // A small prompt docks at the right edge, out of the way, wherever the
  // agent asked for it.
  const placement: ContentFitPlacement = isDockedSize(frameNaturalSize(frameId))
    ? "dock"
    : (request.placement ?? "center");
  const fixed = placement === "center" || placement === "dock";
  const avoidCursor = !fixed;
  const globalExclusions: GlobalPresentationRect[] = [
    ...(request.frameExclusionRects ?? []),
  ];
  // Incidental cursor guidance must not relocate a centered deliverable.
  // Explicit subject exclusions still participate in content-aware fitting.
  if (avoidCursor && globalExclusions.length === 0 && request.cursorPoint) {
    globalExclusions.push({
      x: request.cursorPoint.x,
      y: request.cursorPoint.y,
      width: 1,
      height: 1,
      displayId: request.cursorPoint.displayId,
    });
  }
  const bubble = avoidCursor ? measureAgentCursorBubble(request.agentId) : null;
  if (bubble) {
    globalExclusions.push({
      x: origin.x + bubble.left,
      y: origin.y + bubble.top,
      width: bubble.width,
      height: bubble.height,
      displayId: options.targetDisplayId,
    });
  }
  // Centered and docked frames need their fit descriptor and display owner
  // even before DOM mount. Other unmeasured placements retain their
  // window-mode fallback, including explicit left/right half-screen
  // snapping, unless their body mounts after entry and the fit is measured
  // again then.
  if (
    !fixed &&
    !measured.mounted &&
    !options.mountsLater &&
    !request.desiredContentSize &&
    globalExclusions.length === 0
  ) {
    return undefined;
  }
  const exclusions = localFramePresentationExclusions(globalExclusions);
  const surface = currentFramePresentationSurface(request.fitToDisplay);
  const descriptor = {
    frameId,
    fitToDisplay: request.fitToDisplay,
    targetDisplayId: options.targetDisplayId ?? getWindowDisplayId(),
    desired: measured.desired,
    exclusions: globalExclusions,
    placement,
  };
  return {
    descriptor,
    mounted: measured.mounted,
    box: calculateContentAwareFrameBox({
      surface,
      desired: descriptor.desired,
      exclusions,
      placement,
    }),
  };
}

export function matchingFocusPresentationIds(frameIds: string[]) {
  const presentation = usePresentationStore.getState().presentation;
  if (presentation?.variant !== "focus") return null;
  if (
    frameIds.length !== presentation.frameIds.length ||
    !presentation.frameIds.every((id, index) => frameIds[index] === id)
  ) {
    return null;
  }
  return new Set(presentation.frameIds);
}

export function enterAgentFocusPresentation(
  frameIds: string[],
  request: PresentFramesRequest,
  options: {
    targetDisplayId?: number | null;
    allowMirrorDesktopFocus?: boolean;
    preservedFocusIds?: Set<string> | null;
    /** Desktop Compact: several frames enter as one agent round. */
    asRound?: boolean;
  } = {},
) {
  const activeId = frameIds[0];
  if (!activeId) return;
  const before = usePresentationStore.getState().presentation;
  const { framesById } = useFramesStore.getState();
  const activeIsConversation =
    framesById[activeId]?.frameType === "conversation";
  const focusIds = frameIds.filter((id) => {
    const frame = framesById[id];
    return (
      !!frame && (frame.frameType === "conversation") === activeIsConversation
    );
  });
  const screenshotContext = request.context === "screenshot";
  const mode = focusWindowModeForRequest(request, options.targetDisplayId);
  // The overlay mounts a focus window's body only after focus opens; its
  // content fit is measured again then.
  const mountsLater = focusRendererKind() === "overlay";
  const contentAwareFit =
    !activeIsConversation && focusIds.length === 1
      ? contentAwareFitForRequest(activeId, request, {
          targetDisplayId: options.targetDisplayId,
          mountsLater,
        })
      : undefined;
  // A docked prompt keeps its edge: no half-screen snap.
  const windowMode =
    contentAwareFit?.descriptor.placement === "dock" ? null : mode;
  const refitsAfterMount =
    mountsLater && !!contentAwareFit && !contentAwareFit.mounted;
  const asRound =
    !!options.asRound && focusIds.length > 1 && !activeIsConversation;

  unhideCanvasFrames(focusIds);
  // Armed before the presentation write: the window can mount within it.
  if (refitsAfterMount) {
    armAgentContentRefit(activeId, request.desiredContentSize);
  }
  if (!options.preservedFocusIds) {
    // A round is planned before it enters: one motion, its own boxes.
    const round = asRound
      ? planAgentRound(focusIds, request, EMPTY_FOCUS)
      : null;
    enterPresentationMode("focus", round?.frameIds ?? focusIds, activeId, {
      focusWindowMode: focusIds.length === 1 ? windowMode : null,
      focusLayoutBoxes: contentAwareFit ? [contentAwareFit.box] : undefined,
      customFocusLayout: round?.customFocusLayout,
      agentContentFit: contentAwareFit?.descriptor,
      allowDesktopMirror: options.allowMirrorDesktopFocus,
    });
    stampAgentRound(request, before);
    if (screenshotContext && !shouldUseDesktopExpandedPresentation()) {
      useDesktopMode.getState().openPeek("agent_frame_action");
    }
    return;
  }

  restoreFocusHiddenFramesFor([...options.preservedFocusIds]);
  if (contentAwareFit) {
    usePresentationStore.setState((state) => ({
      presentation: state.presentation
        ? {
            ...state.presentation,
            agentContentFit: contentAwareFit.descriptor,
          }
        : state.presentation,
    }));
    applyFocusLayoutToFrames(activeId, {
      boxes: [contentAwareFit.box],
      duration: 0,
      focusWindowMode: windowMode,
    });
  }
  stampAgentRound(request, before);
  const running = usePresentationStore.getState().presentation;
  const round =
    asRound && running ? planAgentRound(focusIds, request, running) : null;
  if (round) commitPresentation(round, { duration: 0 });
  focusPresentedFramesInDesktopMode([...options.preservedFocusIds], {
    allowMirror: options.allowMirrorDesktopFocus,
  });
}

/** The content size an agent asked for its active frame, measured. */
function roundDesired(activeId: string, request: PresentFramesRequest) {
  return request.desiredContentSize
    ? measureAgentContent(
        activeId,
        request.desiredContentSize,
        request.fitToDisplay,
      ).desired
    : undefined;
}

/** A presentation with nothing yet, to plan an entering round on. */
const EMPTY_FOCUS: PresentationState = {
  variant: "focus",
  renderer: "overlay",
  frameIds: [],
  activeIndex: 0,
  boxes: [],
  naturalSizes: {},
};

/**
 * Several frames an agent presents as one round on `base` (plan 13): one
 * compact group, the active frame main, the others beside it. Null when
 * nobody owns the presentation (no agent): the focus lays them out itself.
 */
function planAgentRound(
  frameIds: string[],
  request: PresentFramesRequest,
  base: PresentationState,
): PresentationState | null {
  const owner = focusRoundOwnerOf(request);
  const activeId = frameIds[0];
  if (!owner || !activeId) return null;
  return planFocusRound(
    base,
    {
      frameIds,
      owner,
      keep: false,
      origin: focusOriginOf(request),
      desired: roundDesired(activeId, request),
    },
    currentFocusPlanEnv(),
  );
}

function revealFrames(frameIds: string[]) {
  const { framesById } = useFramesStore.getState();
  for (const id of frameIds) {
    const frame = framesById[id];
    if (frame && isFrameHiddenByMetadata(frame)) setFrameHidden(id, false);
  }
  unhideCanvasFrames(frameIds);
}

/**
 * Present into the running focus as a round (plan 13), directly rather
 * than as the user's open, so it never becomes the user's Recent. False
 * when nothing of the running focus stays: the caller starts focus over.
 */
export function presentAgentRound(
  frameIds: string[],
  request: PresentFramesRequest,
  options: { allowMirrorDesktopFocus?: boolean } = {},
): boolean {
  const presentation = usePresentationStore.getState().presentation;
  const activeId = frameIds[0];
  if (presentation?.variant !== "focus" || !activeId) return false;
  const owner = focusRoundOwnerOf(request);
  const keep = request.visibility === "keep";
  if (!owner && !keep) return false;
  const env = currentFocusPlanEnv();
  const round = {
    frameIds,
    owner,
    keep,
    replacesRound: request.replacesRound,
  };
  if (framesKeptByRound(presentation, round, env).length === 0) return false;
  const next = planFocusRound(
    presentation,
    {
      ...round,
      origin: focusOriginOf(request),
      desired: roundDesired(activeId, request),
    },
    env,
  );
  if (!next) return false;
  revealFrames(frameIds);
  commitPresentation(next, { duration: 0 });
  focusPresentedFramesInDesktopMode(next.frameIds, {
    allowMirror: options.allowMirrorDesktopFocus,
  });
  return true;
}

/** An agent's `clear` of frames: only its owner's leave; others' and the user's stay. */
export function clearAgentFocusFrames(ownerId: string): void {
  const presentation = usePresentationStore.getState().presentation;
  if (presentation?.variant !== "focus") return;
  const planned = planClearAgentFrames(
    presentation,
    ownerId,
    currentFocusPlanEnv(),
  );
  if (!planned) return;
  if (planned.kind === "exit") {
    exitPresentationMode();
    return;
  }
  commitPresentation(planned.next, { duration: 0 });
  focusPresentedFramesInDesktopMode(planned.next.frameIds);
}
